/** @jsx isolateComponent */

import React from 'react';

import AsyncIndicator from '@zapier/design-system/src/components/display/AsyncIndicator';
import Button from '@zapier/design-system/src/components/navigation/Button';
import { isolateComponent } from '@zapier/common-utils';

type LoadMoreBarProps = {
  /**
   * Text that is rendred within the bar. This is intentionally typed as a
   * `string` to discourage injection of arbitrary React elements. This
   * component is for rendering _text_.
   */
  children: string;
  /**
   * Indicates if the content this bar refers to is currently loading.
   * Triggers the rendering of a loading indicator.
   */
  isLoading: boolean;
  /** Callback to be executed when the bar is clicked. */
  onClick?: (e: React.SyntheticEvent) => void;
  /** Size of the load more button to render */
  size?: 'xsmall' | 'small' | 'medium';
};

/**
 * A bar that, when clicked, initiates the loading of additional content for
 * the user to view. Usually used to produce an infinite pagination effect.
 *
 * Tags: core-ui
 */
export const LoadMoreBar: React.FC<LoadMoreBarProps> = props => {
  const { children, isLoading, onClick, size } = props;
  const asyncIndicator = <AsyncIndicator color="grey500" dotSize={6} />;
  return (
    <Button
      casing="uppercase"
      color="white"
      disabled={isLoading}
      isFullWidth={true}
      onClick={onClick}
      size={size}
    >
      {isLoading ? asyncIndicator : children}
    </Button>
  );
};

export default LoadMoreBar;
